
import { defineComponent, PropType } from 'vue';

// Services
import { shopifyService } from '../../../../Services/ShopifyService/ShopifyService.service';

// Utils
import { TrapFocus } from '../../../../Utils/TrapFocus/TrapFocus';

// Models
import { ProductOptions, Variant, Product } from '../../CoffeePicker.model';

export default defineComponent({
  props: {
    product: {
      required: true,
      type: Object as PropType<Product>,
    },
  },
  data() {
    return {
      showQuickAdd: false,
      selectedVariant: this.product.variants[0] as Variant,
      quantity: 1,
      productOptions: [] as ProductOptions[],
      availableForSale: this.product.variants[0].availableForSale as boolean,
      trapFocus: null as TrapFocus,
      onClickOutOfWidget: Function,
    };
  },
  mounted() {
    // Set the shared click out event.
    this.onClickOutOfWidget = (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-quick-add-container]') && this.showQuickAdd) {
        this.closeQuickAdd();
      }
    };
  },
  methods: {
    /**
     * Toggles if quick add is open on a product
     */
    toggleQuickAdd(event: Event): void {
      let trapFocus: TrapFocus = this.trapFocus;

      // Create the trap focus instance if it doesn't exist
      if (!this.trapFocus) {
        const quickAddContainer = this.$el.parentElement.querySelector(
          '[data-quick-add-container]'
        );
        trapFocus = new TrapFocus(quickAddContainer, {
          escapeClose: true,
          escapeCloseCB: () => {
            this.closeQuickAdd();
          },
        });
        this.trapFocus = trapFocus;
      }

      // Check if trap focus should be applied.
      const handleTrapFocus = trapFocus.checkKeyboardEvent(event);

      // Show or hide the quick add overlay
      if (this.showQuickAdd) {
        this.closeQuickAdd();
      } else {
        this.openQuickAdd(handleTrapFocus);
      }
    },
    /**
     * Opens quick add overlay.
     */
    openQuickAdd(handleTrapFocus = false): void {
      this.showQuickAdd = true;

      if (handleTrapFocus) {
        // Set Timeout used to allow time for dom to render.
        setTimeout(() => {
          this.trapFocus.attachTrapFocus();
        });
      }

      // Bind the global click out event.
      setTimeout(() => {
        this.bindGlobalClickOut();
      });
    },
    /**
     * Closes the quick add overlay.
     */
    closeQuickAdd(): void {
      this.showQuickAdd = false;
      // Remove focus trap
      if (this.trapFocus.activeState) {
        this.trapFocus.dettachTrapFocus();
      }
      // Remove the global click out event.
      this.dettachGlobalClickOut();
    },
    /**
     * Update the seelcted quantity.
     */
    updateQuantity(event: Event): void {
      const target = event.target as HTMLSelectElement;
      this.quantity = +target.value;
    },
    /**
     * Handle option changes.
     */
    onOptionChange(product: Product): void {
      // Grab the current options
      const selectedOptions = product.options
        .filter((item) => item.selectedValue)
        .map((item) => item.selectedValue);

      // Locate the active variant
      const variant = product.variants.find((variantOption: Variant) => {
        const variantOptions = variantOption.selectedOptions.map(
          (so) => so.values
        );

        // Compare the string arrays
        return (
          JSON.stringify(variantOptions) === JSON.stringify(selectedOptions)
        );
      });

      this.selectedVariant = variant;
      this.availableForSale = variant.availableForSale;
    },
    /**
     * Add a product to the cart
     */
    addToCart() {
      // Ignore if not available for sale
      if (!this.availableForSale) return;

      // Variant ID required by AJAX API is base64 encoded in the GraphQL variant id
      //let variantId = atob(this.selectedVariant.id).replace(
      let variantId = this.selectedVariant.id.replace(
        'gid://shopify/ProductVariant/',
        ''
      );

      shopifyService
        .addToCart({
          items: [
            {
              id: variantId,
              quantity: this.quantity,
            },
          ],
        })
        .then(() => {
          this.showQuickAdd = false;
        });
    },
    /**
     * Binds a global click out event.
     */
    bindGlobalClickOut(): void {
      window.addEventListener('click', this.onClickOutOfWidget);
    },
    /**
     * Dettach global click out event.
     */
    dettachGlobalClickOut(): void {
      window.removeEventListener('click', this.onClickOutOfWidget);
    },
  },
  computed: {
    /**
     * Indicates if the meta fields should be shown.
     */
    showMetaFields(): boolean {
      const product: Product = this.product;
      if (product.flavourProfile || product.process || product.varietal) {
        return true;
      }
      return false;
    },
    /**
     * Render the product price.
     */
    productPrice(): string {
      const product: Product = this.product;
      if (product.productType !== 'coffee') {
        if (product.price_varies) {
          return `From: £${product.price.toFixed(2)}`;
        }
        return `£${product.price.toFixed(2)}`;
      }
      return `£${product.price.toFixed(2)}`;
    },
    /**
     * Returns the product url.
     */
    productUrl(): string {
      return `/products/${this.product.handle}`;
    },
    /**
     * Returns the flavour profile for the component.
     */
    flavourProfile(): string {
      if (!this.product.flavourProfile) {
        return ``;
      }

      switch (this.product.flavourProfile.toLocaleLowerCase()) {
        case 'citrus': {
          return `tag-citrus`;
        }
        case 'floral': {
          return `tag-floral`;
        }
        case 'fruity': {
          return `tag-fruity`;
        }
        case 'nutty': {
          return `tag-nutty`;
        }
        case 'spicy': {
          return `tag-spicy`;
        }
        case 'sweet': {
          return `tag-sweet`;
        }
        case 'cocoa': {
          return `tag-cocoa`;
        }
      }
    },
  },
});
